import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// ✅ ADD HEADER + FOOTER
import Header from "../components/Header";
import Footer from "../components/Footer";

const BASE_API_URL = import.meta.env.VITE_API_BASE_URL;

export default function ServiceProviderProfile() {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(null);

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const res = await fetch(
          `${BASE_API_URL || "https://api.home-helper.com"}/api/providers/${id}`
        );

        if (!res.ok) {
          throw new Error("Provider not found");
        }

        const data = await res.json();
        setProvider(data.provider);

        // Load gallery images from service_provider_assets
        try {
          const imgRes = await fetch(
            `${BASE_API_URL || "https://api.home-helper.com"}/api/providers/${id}/images`
          );

          if (imgRes.ok) {
            const imgs = await imgRes.json();
            const normalized = Array.isArray(imgs)
              ? imgs
                  .map((img) => img.thumb_url || img.file_url)
                  .filter(Boolean)
              : [];
            setGalleryImages(normalized);
          } else {
            setGalleryImages([]);
          }
        } catch (e) {
          console.error("Failed to load provider images", e);
          setGalleryImages([]);
        }
      } catch (err) {
        console.error("Failed to load provider profile", err);
        setError("Unable to load provider profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProvider();
  }, [id]);

  if (loading) return <div className="p-6">Loading provider…</div>;
  if (error) return <div className="p-6">{error}</div>;
  if (!provider) return <div className="p-6">Provider not found.</div>;

  // ✅ IMAGE PARSING (handles new assets table + legacy image_urls field)
  const legacyImages = provider.image_urls
    ? Array.isArray(provider.image_urls)
      ? provider.image_urls
      : provider.image_urls.split(",")
    : [];

  const images = galleryImages.length > 0 ? galleryImages : legacyImages;

  const primaryCategory = provider.primary_category
    ? provider.primary_category.replace(/-/g, " ")
    : null;

  const secondaryCategories = provider.secondary_categories
    ? provider.secondary_categories
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean)
    : [];

  return (
    <>
      <Header />

      {/* HERO / HEADER */}
      <div className="provider-hero">
        <div className="provider-hero-inner">
          {/* LOGO */}
          <div className="provider-logo-wrap">
            {provider.logo_url ? (
              <img
                src={provider.logo_url}
                alt="logo"
                className="provider-logo-img"
              />
            ) : (
              <span className="provider-logo-placeholder">No Logo</span>
            )}
          </div>

          {/* COMPANY INFO */}
          <div className="provider-hero-copy">
            <h1 className="provider-hero-title">
              {provider.company_name}
            </h1>
            <div className="provider-hero-tags">
              {primaryCategory && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                  {primaryCategory}
                </span>
              )}
              {secondaryCategories.map((c, i) => (
                <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                  {c.replace(/-/g, " ")}
                </span>
              ))}
              {!primaryCategory && secondaryCategories.length === 0 && (
                <span className="text-gray-500">No categories available</span>
              )}
            </div>
            <p className="provider-hero-serving">
              Serving: {provider.service_zipcodes}
            </p>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="provider-main">
        {/* LEFT: TABLE OF PROVIDER DETAILS (75%) */}
        <div className="provider-main-left">
          <div className="provider-photo-card">
            <h2 className="provider-photo-title">Project Photos</h2>
            {images.length > 0 ? (
              <div className="provider-photo-grid">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    className="provider-photo-thumb-wrap"
                    onClick={(e) => {
                      e.preventDefault();
                      setModalIndex(i);
                    }}
                  >
                    <img
                      src={img.trim()}
                      alt={`project-thumb-${i}`}
                      className="provider-photo-thumb"
                    />
                  </button>
                ))}
              </div>
            ) : (
              <p className="provider-photo-empty">
                No project photos have been added yet.
              </p>
            )}
          </div>

          <div className="provider-table-card">
            <table className="provider-table">
              <tbody>
                <tr>
                  <th className="bg-gray-50 w-48 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Company Name
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.company_name}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Primary Category
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {primaryCategory || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Other Categories
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {secondaryCategories.length > 0
                      ? secondaryCategories
                          .map((c) => c.replace(/-/g, " "))
                          .join(", ")
                      : "N/A"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Service Areas
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.service_zipcodes || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Phone
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.phone || "Not listed"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Email
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.email || "Not listed"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Website
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.website_url ? (
                      <a
                        href={provider.website_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                      >
                        {provider.website_url}
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Founded
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.founding_year || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Locations
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.locations_count || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Revenue
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.annual_revenue || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Programs
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.integration_programs || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 align-top text-left px-4 py-3 font-semibold border-b border-gray-200">
                    About This Contractor
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.description || "No description provided yet."}
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold">
                    Request Quote
                  </th>
                  <td className="px-4 py-3">
                    <button className="primaryButton provider-request-button">
                      Request Quote
                    </button>
                  </td>
                </tr>
                <tr>
                  <th className="bg-gray-50 text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Address
                  </th>
                  <td className="px-4 py-3 border-b border-gray-200">
                    {provider.address || "N/A"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* (Gallery moved into hero header) */}
        </div>

        {/* RIGHT: ZIP SEARCH + CTA (DUMMY, 25%) */}
        <div className="provider-main-right">
          <div className="provider-cta-card">
            <h2 className="provider-cta-title">
              Explore Other Contractors
            </h2>
            <div className="provider-cta-field">
              <label
                htmlFor="zip-search-placeholder"
                className="provider-cta-label"
              >
                Search by ZIP code
              </label>
              <input
                id="zip-search-placeholder"
                type="text"
                placeholder="Enter ZIP code"
                className="provider-cta-input"
              />
            </div>
            <button
              type="button"
              className="provider-cta-button primaryButton"
            >
              Look at other contractors for your project type
            </button>
            <p className="provider-cta-note">
              This search is a placeholder only and is not wired up yet.
            </p>
          </div>
        </div>
      </div>

      {/* IMAGE MODAL */}
      {modalIndex !== null && images[modalIndex] && (
        <div
          className="provider-modal-backdrop"
          onClick={() => setModalIndex(null)}
        >
          <div
            className="provider-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="provider-modal-close"
              onClick={() => setModalIndex(null)}
            >
              
            </button>

            <button
              type="button"
              className="provider-modal-arrow provider-modal-arrow--left"
              onClick={() =>
                setModalIndex((prev) =>
                  prev === null
                    ? 0
                    : (prev - 1 + images.length) % images.length
                )
              }
            >
              
            </button>

            <img
              src={images[modalIndex].trim()}
              alt={`project-full-${modalIndex}`}
              className="provider-modal-image"
            />

            <button
              type="button"
              className="provider-modal-arrow provider-modal-arrow--right"
              onClick={() =>
                setModalIndex((prev) =>
                  prev === null
                    ? 0
                    : (prev + 1) % images.length
                )
              }
            >
              
            </button>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}